import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../App";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GuidanceContext = createContext(null);

export const useGuidance = () => {
  const ctx = useContext(GuidanceContext);
  if (!ctx) return { state: null, loading: true, refresh: () => {}, dismissHint: () => {}, markWelcomeSeen: () => {} };
  return ctx;
};

// Progress nudge messages
const STEP_NUDGES = {
  goal: "Nice. You've set your direction.",
  income: "Income added — now we know what you're working with.",
  plan: "Your money has a plan now.",
  transactions: "Transactions reviewed — you're staying on top of things.",
  overview: "You're all set up. Everything's in one place.",
};

export function GuidanceProvider({ children }) {
  const { user, household } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const prevStepsRef = useRef(null);

  const fetchState = useCallback(async () => {
    if (!user || !household) {
      setState(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/guidance/state`, { credentials: 'include' });
      if (res.ok) {
        const newState = await res.json();

        // Check if any step just became complete (progress nudge)
        if (prevStepsRef.current && newState.steps) {
          for (const [key, step] of Object.entries(newState.steps)) {
            const prevStep = prevStepsRef.current[key];
            if (step.done && prevStep && !prevStep.done && STEP_NUDGES[key]) {
              toast.success(STEP_NUDGES[key]);
            }
          }
        }
        prevStepsRef.current = newState.steps ? { ...newState.steps } : null;

        setState(newState);
      }
    } catch (e) {
      console.error('[Guidance] Failed to fetch state:', e);
    } finally {
      setLoading(false);
    }
  }, [user, household]);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  const markWelcomeSeen = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/guidance/welcome-seen`, {
        method: 'POST',
        credentials: 'include',
      });
      setState(prev => prev ? { ...prev, welcome_seen: true } : prev);
    } catch (e) {
      console.error('[Guidance] Failed to mark welcome seen:', e);
    }
  }, []);

  const dismissHint = useCallback(async (hintId) => {
    try {
      await fetch(`${API_URL}/api/guidance/dismiss`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hint_id: hintId }),
      });
      setState(prev => prev ? {
        ...prev,
        dismissed_hints: [...(prev.dismissed_hints || []), hintId],
      } : prev);
    } catch (e) {
      console.error('[Guidance] Failed to dismiss hint:', e);
    }
  }, []);

  return (
    <GuidanceContext.Provider value={{
      state,
      loading,
      refresh: fetchState,
      dismissHint,
      markWelcomeSeen,
    }}>
      {children}
    </GuidanceContext.Provider>
  );
}
